declare module 'axios' {
  export interface AxiosRequestConfig {
    suppressErrorToast?: boolean;
  }
}
import { createClient, createConfig } from '@hey-api/client-axios';
import { OutsetaAudEnum, OutsetaCountryEnum } from '@models/common/outsetaRealm.enum';
import { IOutsetaToken } from '@models/outseta/outseta';
import i18next from 'i18next';
import { jwtDecode } from 'jwt-decode';
import { toast } from 'react-toastify';
import { attachAnalyticsConsentHeader } from 'src/libs/api/analytics-consent';
import { ACCOUNT_SCOPE_DENIED_EVENT } from 'src/utils/accountScope';
import { AiCreditLimitError, AI_CREDIT_LIMIT_MESSAGE } from 'src/utils/ai-credits';
import { getAuthToken, isClerkAuthEnabled } from 'src/utils/clerkAuth';
import localStorageKeys from 'src/utils/localStorageKeys';

const getOutsetaToken = (): string | null => localStorage.getItem('Outseta.nocode.accessToken');

// ---------------------------------------------------------------------------
// AI Client (@webidoo-eng/jooice-ai)
// ---------------------------------------------------------------------------
const AI_BASE_URL = `${import.meta.env.VITE_JOOICE_AI_URL}`;

export const aiClient = createClient(createConfig({ baseURL: AI_BASE_URL, throwOnError: true }));

aiClient.instance.interceptors.request.use(async (config) => {
  const token = await getAuthToken();
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// ---------------------------------------------------------------------------
// Policy Client (@webidoo-eng/jooice-policy)
// ---------------------------------------------------------------------------
// SDK endpoints already include /v1 prefix — do NOT append VITE_POLICY_API_VERSION
const POLICY_BASE_URL = `${import.meta.env.VITE_POLICY_URL}`;
const POLICY_FRONTEND_KEY = `${import.meta.env.VITE_POLICY_FRONTEND_KEY}`;

export const policyClient = createClient(createConfig({ baseURL: POLICY_BASE_URL }));

function resolveOutsetaCountry(): string {
  const token = getOutsetaToken();
  if (token) {
    const decoded: IOutsetaToken = jwtDecode(token);
    switch (decoded.aud) {
      case OutsetaAudEnum.SPARTACUS:
        return OutsetaCountryEnum.SPARTACUS;
      case OutsetaAudEnum.ITA:
        return OutsetaCountryEnum.ITA;
      case OutsetaAudEnum.MEA:
        return OutsetaCountryEnum.MEA;
      case OutsetaAudEnum.USA:
        return OutsetaCountryEnum.USA;
      default:
        return OutsetaCountryEnum.USA;
    }
  }
  return localStorage.getItem('country_preference') || OutsetaCountryEnum.USA;
}

policyClient.instance.interceptors.request.use(async (config) => {
  config.headers = config.headers || {};
  if (isClerkAuthEnabled()) {
    const token = await getAuthToken();
    if (token) config.headers.Authorization = `Bearer ${token}`;
    else delete config.headers.Authorization;
    const market = localStorage.getItem(localStorageKeys.commons.countryPreference);
    if (market) config.headers['X-Outseta-Country'] = market;
    else delete config.headers['X-Outseta-Country'];
  } else {
    config.headers.Authorization = `${POLICY_FRONTEND_KEY}`;
    config.headers['X-Outseta-Country'] = resolveOutsetaCountry();
  }
  return config;
});

policyClient.instance.interceptors.response.use(
  (response) => response,
  (error) => {
    if (!error.config?.suppressErrorToast) {
      if (error.response?.data?.message) {
        toast(error.response.data.message, { type: 'error' });
      } else if (error.message) {
        toast(error.message, { type: 'error' });
      }
    }
    throw error;
  },
);

// ---------------------------------------------------------------------------
// Listing-Review / Backend Client (@webidoo-eng/jooice-listing-review)
// ---------------------------------------------------------------------------
const BACKEND_BASE_URL = `${import.meta.env.VITE_BACKEND_URL}`;

export const listingReviewClient = createClient(createConfig({ baseURL: BACKEND_BASE_URL }));

listingReviewClient.instance.interceptors.request.use(async (config) => {
  config.headers.set?.('Accept-Language', i18next.language);
  const token = await getAuthToken();
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return attachAnalyticsConsentHeader(config);
});

export const MEMBER_EMAIL_LINK_PENDING = 'MEMBER_EMAIL_LINK_PENDING';

export async function handleListingReviewResponseError(error: {
  response?: { status?: number; data?: { message?: string; code?: string } };
  config?: { url?: string };
}): Promise<never> {
  if (error.response?.status === 409 && error.response.data?.code === MEMBER_EMAIL_LINK_PENDING) {
    // The server message is for logs only; it must never reach the user, so callers that render `message` get our copy.
    await i18next.loadNamespaces('errors');
    const message = i18next.t('errors:member-email-link-pending');
    toast.error(message, { toastId: MEMBER_EMAIL_LINK_PENDING });
    throw { ...error.response.data, message };
  }

  if (error.response?.status === 401 && !error.config?.url?.includes('magic-link')) {
    // Clerk owns session expiry; a route can reject Clerk while its backend
    // auth opt-in is pending, so preserve the active Clerk session.
    if (isClerkAuthEnabled()) throw error.response?.data;

    const countryCode = localStorage.getItem(localStorageKeys.commons.countryPreference);
    localStorage.clear();
    sessionStorage.clear();

    sessionStorage.setItem('logout_reason', 'token_expired');
    window.location.href = `/auth${countryCode && countryCode !== 'null' ? `?country=${countryCode}` : ''}`;
  }

  if (error.response?.status === 402 && !error.config?.url?.includes('magic-link')) {
    toast.error(error.response.data?.message ?? 'Payment required');
  }

  // AccountScopeDeniedException answers 404 by default (it must not reveal whether the resource exists), so 404 counts too.
  const isScopeStatus = error.response?.status === 403 || error.response?.status === 404;

  if (isScopeStatus && error.response?.data?.message?.toLowerCase().includes(' for account')) {
    window.dispatchEvent(new Event(ACCOUNT_SCOPE_DENIED_EVENT));
  }

  if (error.response?.status === 403) {
    toast.error(error.response?.data?.message || 'Access denied');
  }

  throw error.response?.data;
}

listingReviewClient.instance.interceptors.response.use((response) => response, handleListingReviewResponseError);

// ---------------------------------------------------------------------------
// Streaming helper (SSE via native fetch — SDK doesn't support SSE)
// ---------------------------------------------------------------------------
export async function aiStream(
  endPoint: string,
  body: object,
): Promise<ReadableStreamDefaultReader<Uint8Array> | undefined> {
  const token = await getAuthToken();
  const response = await fetch(`${AI_BASE_URL}${endPoint}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(body),
  });

  const readErrorResponse = async (res: Response): Promise<{ rawMessage: string; displayMessage: string }> => {
    const fallback = `Jooice AI error (HTTP ${res.status})`;
    try {
      const parsed = (await res.json()) as { message?: unknown };
      const rawMessage = typeof parsed.message === 'string' ? parsed.message : '';
      const displayMessage = rawMessage.trim() || fallback;
      return { rawMessage, displayMessage };
    } catch {
      return { rawMessage: '', displayMessage: fallback };
    }
  };

  if (!response.ok) {
    const { rawMessage, displayMessage } = await readErrorResponse(response);
    if (rawMessage === AI_CREDIT_LIMIT_MESSAGE && (response.status === 402 || response.status === 403)) {
      throw new AiCreditLimitError(AI_CREDIT_LIMIT_MESSAGE, response.status);
    }

    toast(displayMessage);
    throw new Error(displayMessage);
  }

  if (response.body) {
    return response.body.getReader();
  }
}

export interface SseFrame {
  event: string;
  data: string;
}

/**
 * Reassembles SSE frames (`event:`/`data:` lines separated by a blank line)
 * out of a raw byte stream, across chunk boundaries.
 */
export async function* parseSseFrames(
  reader: ReadableStreamDefaultReader<Uint8Array>,
  defaultEvent = 'message',
): AsyncGenerator<SseFrame> {
  const decoder = new TextDecoder();
  let buffer = '';

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });
    const frames = buffer.split('\n\n');
    buffer = frames.pop() ?? '';

    for (const frame of frames) {
      let event = defaultEvent;
      let data: string | null = null;

      for (const line of frame.split('\n')) {
        if (line.startsWith('event: ')) {
          event = line.slice(7).trim();
        } else if (line.startsWith('data: ')) {
          data = line.slice(6);
        }
      }

      if (data !== null) yield { event, data };
    }
  }
}
